import React, { useState, useEffect } from 'react';
import { CATEGORY_MAP, config, QuestionCategory } from "../../Const.js";
import QuestionContainer from './QuestionContainer.jsx';
import { CircularProgress, Box } from '@mui/material';
import { executeFetch } from '../../Util.js';
import SearchBox from '../SearchBox.jsx';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';

const Accordion = styled((props) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&:before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === 'dark'
            ? 'rgba(0, 0, 0, .05)'
            : 'rgba(233, 238, 241)',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(0),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(0),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

function DataQuestionBank({ entity, onAddQuestion, quizSubject }) {
    const propEntityId = entity.id ?? '';
    const defaultCategoryList = CATEGORY_MAP[quizSubject] ?? null;

    const [searchedEntityId, setSearchedEntityId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [questionData, setQuestionData] = useState([]);
    const [categoryOverride, setCategoryOverride] = useState(null);
    const [reloadTrigger, setReloadTrigger] = useState(0);

    const effectiveEntityId = searchedEntityId || propEntityId;
    const effectiveCategoryList = categoryOverride ?? defaultCategoryList;

    const handleEntitySelected = (selectedEntity) => {
        if (!selectedEntity) {
            setSearchedEntityId('');
            setCategoryOverride(null);
            setQuestionData([]);
            return;
        }

        const selectedEntityId = selectedEntity.id;
        const currentEntityId = effectiveEntityId;

        if (selectedEntityId === currentEntityId) {
            setCategoryOverride([QuestionCategory.ALL]);
            setReloadTrigger((prev) => prev + 1);
            return;
        }

        setSearchedEntityId(selectedEntityId);
        setCategoryOverride(null);
    };

    const handleDuplicate = (q) => {
        const clonedQuestionData = JSON.parse(JSON.stringify(questionData));
        const clonedQuestion = JSON.parse(JSON.stringify(q));

        clonedQuestion.question.templateId =
            q.question.templateId.split("~~~")[0] + "~~~" + Math.random();

        const index = questionData.findIndex(
            (i) => i.question.templateId === q.question.templateId
        );

        clonedQuestionData.splice(index + 1, 0, clonedQuestion);
        setQuestionData(clonedQuestionData);
    };

    useEffect(() => {
        if (!effectiveEntityId) {
            setQuestionData([]);
            setIsLoading(false);
            return;
        }

        if (effectiveCategoryList != null) {
            setIsLoading(true);

            let categoryUrlSuffix;

            if (effectiveCategoryList[0] === QuestionCategory.ALL) {
                categoryUrlSuffix = "all";
            } else {
                categoryUrlSuffix = "categories/" + effectiveCategoryList.join("|");
            }

            const questionGeneratorUrl =
                `${config.SERVER_URL}/api/v1/admin/generateMCQuestions/${effectiveEntityId}/${categoryUrlSuffix}`;

            const responseHandler = (result) => {
                setIsLoading(false);
                setQuestionData(result ?? []);
            };

            executeFetch(
                questionGeneratorUrl,
                "get",
                null,
                null,
                null,
                responseHandler
            );
        }
    }, [effectiveEntityId, effectiveCategoryList, reloadTrigger]);

    const qList = questionData.map((q) => {
        const textMessage = q.question.textMessage;

        return (
            <Accordion key={q.question.templateId}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel-${q.question.templateId}-content`}
                    id={`panel-${q.question.templateId}-header`}
                >
                    <Typography variant="subtitle1" sx={{ alignItems: 'center' }}>
                        {textMessage}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <QuestionContainer
                        data={q}
                        onAdd={onAddQuestion}
                        selectedMode={false}
                        onDuplicate={handleDuplicate}
                    />
                </AccordionDetails>
            </Accordion>
        );
    });

    return (
        <Box sx={{ height: '100%', pl: '10px', pr: '20px', overflowY: 'auto' }}>
            <SearchBox
                endPoint={'entity'}
                value={entity}
                onEntitySelected={handleEntitySelected}
                resultMappingFunction={(r) => {
                    return { label: r.label, id: r.idMapping.WIKIDATA };
                }}
            />

            <Box sx={{ mt: '20px' }}>
                {isLoading && (
                    <Box sx={{ padding: '10px' }}>
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && qList}
            </Box>
        </Box>
    );
}

export default DataQuestionBank;